// utils/OperatorContext.js
import { createContext, useContext, useState, useEffect } from "react";

const OperatorContext = createContext();

/**
 * OperatorProvider — now scoped by optional tvId
 * Each TV keeps its own selectedDay and actions in localStorage.
 */
export function OperatorProvider({ children, tvId = null }) {
  // --- unique storage keys per TV ---
  const keyPrefix = tvId ? `tv${tvId}_` : "";

  const [selectedDay, setSelectedDay] = useState(
    localStorage.getItem(`${keyPrefix}selectedDay`) || "Day-1"
  );
  const [activeActions, setActiveActions] = useState(
    JSON.parse(localStorage.getItem(`${keyPrefix}activeActions`)) || []
  );

  // --- listen for cross-tab or other panel updates ---
  useEffect(() => {
    const handler = (e) => {
      if (e.key === `${keyPrefix}selectedDay`) setSelectedDay(e.newValue);
      if (e.key === `${keyPrefix}activeActions`)
        setActiveActions(JSON.parse(e.newValue));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [keyPrefix]);

  // --- change day for this TV only ---
  const updateDay = (day) => {
    setSelectedDay(day);
    localStorage.setItem(`${keyPrefix}selectedDay`, day);
  };

  // --- dispatch action, include tvId in event payload ---
  const dispatchAction = (actionId, payload = {}) => {
    const fullPayload = tvId ? { ...payload, tvId } : payload;
    const newActions = [
      ...activeActions,
      { id: actionId, payload: fullPayload, ts: Date.now() },
    ];
    setActiveActions(newActions);
    localStorage.setItem(
      `${keyPrefix}activeActions`,
      JSON.stringify(newActions)
    );

    window.dispatchEvent(new CustomEvent(actionId, { detail: fullPayload }));
  };

  return (
    <OperatorContext.Provider
      value={{
        tvId,
        selectedDay,
        updateDay,
        activeActions,
        dispatchAction,
      }}
    >
      {children}
    </OperatorContext.Provider>
  );
}

export function useOperator() {
  return useContext(OperatorContext);
}
